
import { IDBGroup, IDBSmartCode, IDBUser, LOGIN_FRAGMENT, saveAuthenticationData, useDomain, USER_FRAGMENT, useRegisterUser } from "@netfront/gelada-identity-library";
import { Button, Checkbox, ErrorIcon, FlexContainer, Input, InputFieldWrapper, PasswordInput, Select, SuccessIcon } from "@netfront/ui-library";
import { useFormik } from "formik";
import { useRouter } from "next/router";
import { ensureAustralianAreaCode, getValidClassNames, shouldIncludePhone } from "../../../utils";
import * as yup from 'yup';

import { IRegisterFormFields, RegisterFormProps } from "./RegisterForm.interfaces";
import Link from "next/link";
import { PasswordValidator } from "@/components/PasswordValidator";
import { REG_EXP_MINIMUM_EIGHT_CHARACTERS, REG_EXP_NUMBER, REG_EXP_ONE_LOWERCASE, REG_EXP_ONE_UPPERCASE, REG_EXP_SPECIAL_CHARACTER } from "@/components/PasswordValidator/PasswordValidator.constants";
import { ICookieOptionalAttributes, setCookie } from "@netfront/common-library";
import { InformationBanner } from "@/components/InformationBanner";
import { CONSTANTS } from "@/constants";
import { useToast } from "@/hooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation } from "@netfront/apollo-providers";
import { gql } from "@apollo/client";
import type { PublicMutation, PublicMutationRegisterWithoutActivationArgs } from "@netfront/api-gelada-public-types";

const getRegisterSchema = ({ 
  userTypeId, 
  isSmartCodeValid,
  groupEmailSuffixes = []
}: {
  userTypeId: number;
  isSmartCodeValid?: boolean;
  groupEmailSuffixes?: string[]; 
}) =>
  yup.object({
    email: groupEmailSuffixes.length > 0
      ? yup.string()
          .required('Please enter your email address')
          .test('valid-email-with-suffix', 'Please enter a valid email address', function(value) {
            const { emailSuffix } = this.parent;
            const suffix = emailSuffix || (groupEmailSuffixes.length === 1 ? groupEmailSuffixes[0] : '');
            if (!suffix || !value) return true;
            return yup.string().email().isValidSync(`${value}@${suffix}`);
          })
      : yup.string().email('Please enter a valid email address').required('Please enter in a valid email address'),
    confirmEmail: groupEmailSuffixes.length > 0
      ? yup.string()
          .required('Please confirm your email address')
          .oneOf([yup.ref('email')], 'Email addresses must match')
          .test('valid-confirm-email-with-suffix', 'Please enter a valid email address', function(value) {
            const { emailSuffix } = this.parent;
            const suffix = emailSuffix || (groupEmailSuffixes.length === 1 ? groupEmailSuffixes[0] : '');
            if (!suffix || !value) return true;
            return yup.string().email().isValidSync(`${value}@${suffix}`);
          })
      : yup.string().oneOf([yup.ref('email')], 'Email addresses must match').required('Please confirm your email address'),
    firstName: yup.string().required('Please enter your first name'),
    smartCode: isSmartCodeValid === false
      ? yup.string().required('Please enter your access code').test('smart-code-valid', 'Code is not valid', () => false)
      : yup.string().required('Please enter your access code'),
    lastName: yup.string().required('Please enter your last name'),
    password: yup
      .string()
      .required('Please enter in a password')
      .matches(REG_EXP_MINIMUM_EIGHT_CHARACTERS, "Password must be at least 8 characters")
      .matches(REG_EXP_ONE_LOWERCASE, "Password must contain at least one lowercase letter")
      .matches(REG_EXP_ONE_UPPERCASE, "Password must contain at least one uppercase letter")
      .matches(REG_EXP_NUMBER, "Password must contain at least one number")
      .matches(REG_EXP_SPECIAL_CHARACTER, "Password must contain at least one special character"),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match')
      .required('Please confirm your password'),
    hasAcceptedTerms: yup.boolean().oneOf([true], 'Please read and accept the terms and conditions').required(),
    emailSuffix: groupEmailSuffixes.length > 0
      ? yup.string().required('Please select an email suffix')
      : yup.string().notRequired(),

    phoneNumber:
      shouldIncludePhone(userTypeId)
        ? yup
          .string()
          .transform((value) => value.replace(/\s+/g, ''))
          .matches(
            /^(\+61[2-478]\d{8}|0[2-478]\d{8})$/,
            'Please enter a valid Australian phone number'
          )
          .required('Required')
        : yup.string().notRequired(),

    confirmPhoneNumber:
      shouldIncludePhone(userTypeId)
        ? yup
          .string()
          .transform((value) => value.replace(/\s+/g, ''))
          .oneOf([yup.ref('phoneNumber')], 'Phone numbers must match')
          .required('Please confirm your phone number')
        : yup.string().url('Enter a valid URL').notRequired(),
  });
  

export const RegisterForm = ({ userTypeId }: RegisterFormProps) => {
  const { USER_TYPE } = CONSTANTS;

  const { push, query } = useRouter();
  const { getDomain } = useDomain();
  const { handleToastError } = useToast();

  // Guards against duplicate registration requests from repeated clicks / Enter presses.
  // Student/teacher: a duplicate submit awaits the in-flight request so Formik keeps isSubmitting true.
  // Parent: handleRegisterUser isn't awaitable, so loading is covered by isRegisterUserLoading instead.
  const registerWithoutActivationRequestRef = useRef<Promise<void> | null>(null);
  const isRegisterUserInFlightRef = useRef(false);

  const [groupEmailSuffixes, setGroupEmailSuffixes] = useState<string[]>([]);
  const [isSmartCodeValid, setIsSmartCodeValid] = useState<boolean | undefined>(undefined);
  const [isCodeFromUrl, setIsCodeFromUrl] = useState(false);

  const validationSchema = useMemo(() => getRegisterSchema({ userTypeId, isSmartCodeValid, groupEmailSuffixes}), [userTypeId, groupEmailSuffixes, isSmartCodeValid]);

  const { handleMutation: registerWithoutActivation } = useMutation<
  Pick<PublicMutation, 'registerWithoutActivation'>, PublicMutationRegisterWithoutActivationArgs
  >({
    apiName: 'gelada',
    // Public mutation: don't attach (possibly stale) auth tokens from a previous session
    isPublic: true,
    // handleMutation swallows errors and resolves undefined, so surface them here
    onError: (error) => {
      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
    mutation: gql`
      mutation registerWithoutActivation(
        $user: RegisterUserInputType!, 
        $credential: CredentialInput!
        ) {
        registerWithoutActivation(
          user: $user
          credential: $credential
        ) {
          refreshToken
          token
          user {
            id
            firstName: firstname
            lastName: lastname
            credential {
              email
            }
          }
        }
      }

    `
  });

  const handleRegisterWithoutActivation = async ({ email, emailSuffix, firstName, lastName, password, smartCode }: IRegisterFormFields) => {
    try {
      const credentialEmail = groupEmailSuffixes.length > 0 ? `${email}@${emailSuffix}` : email;
      const data = await registerWithoutActivation({
        credential: { email: credentialEmail, password },
        user: { firstname: firstName, lastname: lastName, smartCode: smartCode.replace(/\s+/g, "") }
      });

      // undefined => request failed and the error has already been shown via onError
      if (!data) return;

      if (!data.registerWithoutActivation) {
        throw new Error("Registration did not return any data");
      }

      const { user, token: accessToken, refreshToken } = data.registerWithoutActivation;

      if (!user || !accessToken || !refreshToken) {
        throw new Error("Registration returned incomplete authentication data")
      }

      const domain = getDomain();
      const isSecure = process.env.REACT_APP_COOKIE_ATTRIBUTE_SECURE === 'true';
      const sameSite: ICookieOptionalAttributes['sameSite'] = isSecure ? 'None' : 'Lax';

      await saveAuthenticationData({
        accessToken,
        accessTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        refreshToken,
        refreshTokenOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
        user: user as unknown as IDBUser,
        userOptionalCookieAttributes: {
          domain,
          sameSite,
          secure: isSecure,
        },
      });

      await push('/dashboard');
    } catch (error) {
      handleToastError({
        error: error as Error,
        shouldUseFriendlyErrorMessage: true,
      });
    }
  };

  const formik = useFormik<IRegisterFormFields>({
    validateOnBlur: true,
    initialValues: {
      email: '',
      confirmEmail: '',
      smartCode: '',
      firstName: '',
      hasAcceptedTerms: false,
      lastName: '',
      password: '',
      phoneNumber: '',
      confirmPhoneNumber: '',
      confirmPassword: '',
      emailSuffix: ''
    },
    validationSchema,
    onSubmit: async (submittedValues: IRegisterFormFields) => {
      const { email, firstName, lastName, password, smartCode } = submittedValues;

      // Await the in-flight request rather than returning early, so Formik keeps isSubmitting true
      if (registerWithoutActivationRequestRef.current) return registerWithoutActivationRequestRef.current;
      if (isRegisterUserInFlightRef.current) return;

      if(userTypeId === USER_TYPE.STUDENT.ID || userTypeId === USER_TYPE.TEACHER.ID) {
        registerWithoutActivationRequestRef.current = handleRegisterWithoutActivation(submittedValues).finally(() => {
          registerWithoutActivationRequestRef.current = null;
        });

        return registerWithoutActivationRequestRef.current;
      }

      isRegisterUserInFlightRef.current = true;

      handleRegisterUser({
        credential: {
          email,
          password,
        },
        user: {
          firstname: firstName,
          lastname: lastName,
          smartCode: smartCode.replace(/\s+/g, ""),
        },
      });
    }
  });

  const { errors, touched, values, isSubmitting, handleChange, handleSubmit, handleBlur } = formik;

  const { handleRegisterUser, isLoading: isRegisterUserLoading = false } = useRegisterUser({
    onCompleted: () => {
      isRegisterUserInFlightRef.current = false;

      if(shouldIncludePhone(userTypeId)) {
        setCookie({
          name: CONSTANTS.COOKIES.PHONE_NUMBER,
          value: String(ensureAustralianAreaCode(values.phoneNumber)),
          domain: getDomain(),
          path: '/'
        });
      }
      
      push('/activate-account');
    },
    onError: error => {
      isRegisterUserInFlightRef.current = false;

      handleToastError({
        error,
        shouldUseFriendlyErrorMessage: true,
      });
    },
  });

  const { handleQuery: handleGetEmailSuffixesByCode } = useLazyQuery<
    {
      getSmartCode: Omit<IDBSmartCode, 'groups'> & {
        groups: (IDBGroup & { emailSuffixes: string[] })[];
      };
    },
    {
      projectId: string;
      code: string;
    }
  >({
    apiName: 'gelada',
    isPublic: true,
    query: gql`
      query getEmailSuffixesBySmartCode($projectId: String!, $code: String!) {
        getSmartCode(projectId: $projectId, code: $code) {
          groups {
            emailSuffixes
          }
        }
      }
    `,
    onCompleted: ({ getSmartCode }) => {
      if (!getSmartCode) {
        setIsSmartCodeValid(false);
        setGroupEmailSuffixes([]);
        return;
      }

      const { groups = [] } = getSmartCode;
      setIsSmartCodeValid(true);
      const suffixes = groups.flatMap((group) => group.emailSuffixes ?? []);
      setGroupEmailSuffixes(suffixes);

      if (suffixes.length > 0) {
        formik.setFieldValue('email', formik.values.email.split('@')[0]);
        formik.setFieldValue('confirmEmail', formik.values.confirmEmail.split('@')[0]);
      }
      if (suffixes.length === 1) {
        formik.setFieldValue('emailSuffix', suffixes[0]);
      }
    },
    onError: () => {
      setGroupEmailSuffixes([]);
      setIsSmartCodeValid(false);
    }
  });

  const shouldCheckEmailSuffixes = userTypeId === USER_TYPE.STUDENT.ID;

  const handleEmailPrefixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    formik.setFieldValue(name, value.split('@')[0]);
  };

  useEffect(() => {
    if (userTypeId !== USER_TYPE.TEACHER.ID) return;
    const code = Array.isArray(query.code) ? query.code[0] : query.code;
    if (!code) return;

    formik.setFieldValue('smartCode', code);
    setIsCodeFromUrl(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.code]);

  return (
    <form className="flex flex-col" onSubmit={handleSubmit}>
      {(isCodeFromUrl || userTypeId === USER_TYPE.TEACHER.ID) && (
        <InformationBanner additionalClassNames="shadow-none mb-4">
          <p className="mb-0">
            {isCodeFromUrl
              ? <>Your access code has been pre-filled. Need help? Get in touch at <Link className="color-green-500" href='mailto:info@parentsandteens.org.au'>info@parentsandteens.org.au</Link></>
              : <>Your code is in the Teacher&apos;s &apos;Information Pack&apos; emailed by the Health4Life Team. Need help? Get in touch at <Link className="color-green-500" href='mailto:info@parentsandteens.org.au'>info@parentsandteens.org.au</Link></>
            }
          </p>
        </InformationBanner>
      )}
      {!isCodeFromUrl && (
        <Input
          errorMessage={touched.smartCode && errors.smartCode ? errors.smartCode : ''}
          id='smartCode'
          labelText='Access code'
          name='smartCode'
          placeholder='Enter your access code'
          type='text'
          value={values.smartCode}
          isRequired
          onChange={handleChange}
          onBlur={(event) => {
            handleBlur(event);

            const { target: { value }} = event;

            if (!value) {
              setGroupEmailSuffixes([]);
              return;
            }

            if (shouldCheckEmailSuffixes) {
              handleGetEmailSuffixesByCode({
                projectId: String(process.env.REACT_APP_PROJECT_ID),
                code: value,
              });
            }
          }}
          iconAfter={isSmartCodeValid !== undefined ? {
            icon: isSmartCodeValid ? SuccessIcon : ErrorIcon,
            helperText: isSmartCodeValid ? 'Valid code' : 'Invalid code',
          } : undefined}
        />
      )}

      <Input
        errorMessage={touched.firstName && errors.firstName ? errors.firstName : ''}
        id='firstName'
        labelText='First name'
        name='firstName'
        placeholder='Enter your first name'
        type='text'
        value={values.firstName}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <Input
        errorMessage={touched.lastName && errors.lastName ? errors.lastName : ''}
        id='lastName'
        labelText='Last name'
        name='lastName'
        placeholder='Enter your last name'
        type='text'
        value={values.lastName}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      {Boolean(values.smartCode) && groupEmailSuffixes.length > 0 ? (
        <InputFieldWrapper
            id="id_email_wrapper"
            label="Email"
            labelType="span"
            isContentStacked
          >
            <FlexContainer gap="x-small" isFullWidth>
              <Input
                additionalClassNames="c-input--email-suffix"
                errorMessage={touched.email && errors.email ? errors.email : ''}
                id='email'
                labelText='Email'
                name='email'
                placeholder='Enter email'
                type='text'
                value={values.email}
                isRequired
                isLabelHidden
                onChange={handleEmailPrefixChange}
                onBlur={handleBlur}
                iconAfter={{
                  isTextOnly: true,
                  helperText: `@${groupEmailSuffixes.length === 1 ? groupEmailSuffixes[0] : ''}`
                }}

              />
              {groupEmailSuffixes.length > 1 && (
                <div className="absolute right-0 w-auto">
                  <Select
                    errorMessage={touched.emailSuffix && errors.emailSuffix ? errors.emailSuffix : ''}
                    id="id_email_suffix"
                    labelText="suffix"
                    name="emailSuffix"
                    isLabelHidden
                    options={groupEmailSuffixes.map((suffix) => ({
                      id: suffix,
                      name: suffix,
                      value: suffix,
                    }))}
                    value={values.emailSuffix}
                    onChange={handleChange}
                    isRequired
                  />
                </div>
              )}

            </FlexContainer>
        </InputFieldWrapper>
      ): (
        <Input
          errorMessage={touched.email && errors.email ? errors.email : ''}
          id='email'
          labelText='Email'
          name='email'
          placeholder='Enter email'
          type='email'
          value={values.email}
          isRequired
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      {Boolean(values.smartCode) && groupEmailSuffixes.length > 0 ? (
        <InputFieldWrapper
            id="id_confirm_email_wrapper"
            label="ConfirmEmail"
            labelType="span"
          >
            <FlexContainer gap="x-small" isFullWidth>
              <Input  
                additionalClassNames="c-input--email-suffix"
                errorMessage={touched.confirmEmail && errors.confirmEmail ? errors.confirmEmail : ''}
                id='confirmEmail'
                labelText='Confirm Email'
                name='confirmEmail'
                placeholder='Re-enter email'
                type='text'
                value={values.confirmEmail}
                isRequired
                isLabelHidden
                onChange={handleEmailPrefixChange}
                onBlur={handleBlur}
                iconAfter={{
                  isTextOnly: true,
                  helperText: `@${groupEmailSuffixes.length === 1 ? groupEmailSuffixes[0] : ''}`
                }}
              />
              {groupEmailSuffixes.length > 1 && (
                <div className="absolute right-0 w-auto">
                  <Select
                    id="id_confirm_email_suffix"
                    labelText="suffix"
                    name="emailSuffix"
                    options={groupEmailSuffixes.map((suffix) => ({
                      id: suffix,
                      name: suffix,
                      value: suffix,
                    }))}
                    isLabelHidden
                    value={values.emailSuffix}
                    onChange={handleChange}
                    isRequired
                  />
                </div>
              )}

            </FlexContainer>
        </InputFieldWrapper>
      ): (
        <Input
          errorMessage={touched.confirmEmail && errors.confirmEmail ? errors.confirmEmail : ''}
          id='confirmEmail'
          labelText='Confirm Email'
          name='confirmEmail'
          placeholder='Re-enter your email'
          type='email'
          value={values.confirmEmail}
          isRequired
          onChange={handleChange}
          onBlur={handleBlur}
        />
      )}

      {shouldIncludePhone(userTypeId) && (
        <>
          <Input
            errorMessage={touched.phoneNumber && errors.phoneNumber ? errors.phoneNumber : ''}
            id='phoneNumber'
            labelText='Phone number'
            name='phoneNumber'
            placeholder='Enter your phone number'
            type='text'
            value={values.phoneNumber}
            isRequired
            onChange={handleChange}
            onBlur={handleBlur}
          />   

          <Input
            errorMessage={touched.confirmPhoneNumber && errors.confirmPhoneNumber ? errors.confirmPhoneNumber : ''}
            id='confirmPhoneNumber'
            labelText='Confirm Phone Number'
            name='confirmPhoneNumber'
            placeholder='Re-enter your phone number'
            type='text'
            value={values.confirmPhoneNumber}
            isRequired
            onChange={handleChange}
            onBlur={handleBlur}
          />
       </>
      )}

      <PasswordInput
        errorMessage={touched.password && errors.password ? errors.password : ''}
        id='password'
        labelText='Password'
        name='password'
        placeholder='Enter password'
        value={values.password}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <PasswordInput
        errorMessage={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : ''}
        id='confirmPassword'
        labelText='Repeat password'
        name='confirmPassword'
        placeholder='Enter password'
        value={values.confirmPassword}
        isRequired
        onChange={handleChange}
        onBlur={handleBlur}
      />

      <div className="py-4">
        <div>
          <label className="flex items-center" htmlFor="hasAcceptedTerms">
            <Checkbox
              additionalClassNames={`${errors.hasAcceptedTerms ? '!border-red-800': 'border-accent'}`}
              id="hasAcceptedTerms"
              isChecked={values.hasAcceptedTerms}
              labelText='Terms and conditions'
              name='hasAcceptedTerms'
              value="hasAcceptedTerms"
              isLabelHidden
              onChange={handleChange}
            />

            <p className="ml-3">I agree to the <Link href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="color-green-500 weight-600">terms and conditions</Link></p>
          
          </label>
          {Boolean(touched.hasAcceptedTerms && errors.hasAcceptedTerms) && (
            <div className="mt-4">
              <span
                className={getValidClassNames({
                  'line-1.2': true,
                  'color-red-400': Boolean(errors.hasAcceptedTerms),
                })}
              >
                {errors.hasAcceptedTerms}
              </span>
            </div>
          )}
        </div>
      </div>

      <Button
        additionalClassNames="mt-2"
        isDisabled={isSubmitting || isRegisterUserLoading}
        isLoading={isSubmitting || isRegisterUserLoading}
        text='Register'
        type='submit'
        variant='tertiary'
      />

      <div className="mt-8">
        <PasswordValidator firstPassword={values.password} secondPassword={values.confirmPassword} />
      </div>

      <hr className="my-8" />
      <p>Already have an account? <Link href="/login" className="color-green-500 weight-600">Login</Link></p>

    </form>
  )
};